import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../config';
import './RecruiterProfileSetup.css';

function RecruiterProfileSetup() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Company Basics
    company_name: '',
    industry: '',
    company_size: '',
    founded_year: '',
    company_website: '',
    registration_number: '',
    
    // Step 2: Contact Information
    contact_person_name: '',
    contact_person_title: '',
    phone: '',
    street_address: '',
    city: '',
    province: '',
    postal_code: '',
    
    // Step 3: Company Details
    about_company: '',
    mission_statement: '',
    company_culture: '',
    values: [],
    
    // Step 4: Benefits & Hiring
    benefits: [],
    typical_hiring_timeline: '',
    interview_process: '',
  });

  const [currentValue, setCurrentValue] = useState('');
  const [currentBenefit, setCurrentBenefit] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const provinces = [
    'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
    'Free State', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape'
  ];

  const companySizes = ['1-10', '11-50', '51-200', '201-500', '500+'];
  
  const industries = [
    'Technology', 'Finance', 'Healthcare', 'Education', 'Retail',
    'Manufacturing', 'Consulting', 'Marketing', 'Real Estate', 'Other'
  ];

  const commonBenefits = [
    'Health Insurance', 'Retirement Plan', 'Flexible Hours', 'Remote Work',
    'Professional Development', 'Gym Membership', 'Paid Time Off',
    'Performance Bonus', 'Stock Options', 'Parental Leave'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addValue = () => {
    if (currentValue.trim() && !formData.values.includes(currentValue.trim())) {
      setFormData(prev => ({
        ...prev,
        values: [...prev.values, currentValue.trim()]
      }));
      setCurrentValue('');
    }
  };

  const removeValue = (index) => {
    setFormData(prev => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== index)
    }));
  };

  const toggleBenefit = (benefit) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.includes(benefit)
        ? prev.benefits.filter(b => b !== benefit)
        : [...prev.benefits, benefit]
    }));
  };

  const addCustomBenefit = () => {
    if (currentBenefit.trim() && !formData.benefits.includes(currentBenefit.trim())) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, currentBenefit.trim()]
      }));
      setCurrentBenefit('');
    }
  };

  const removeBenefit = (benefit) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter(b => b !== benefit)
    }));
  };

  const handleNext = () => {
    // Validate current step
    if (currentStep === 1) {
      if (!formData.company_name || !formData.industry) {
        setError('Please fill in all required fields');
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.contact_person_name || !formData.phone) {
        setError('Please fill in all required fields');
        return;
      }
    }
    setError('');
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(prev => prev - 1);
  };

  const handleSkip = () => {
    setError('');
    setCurrentStep(prev => prev + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Your session has expired. Please log in again.');
        setLoading(false);
        return;
      }

      const profileData = {
        company_name: formData.company_name,
        industry: formData.industry,
        company_size: formData.company_size,
        founded_year: formData.founded_year ? parseInt(formData.founded_year) : null,
        company_website: formData.company_website,
        registration_number: formData.registration_number,
        contact_person_name: formData.contact_person_name,
        contact_person_title: formData.contact_person_title,
        phone: formData.phone,
        street_address: formData.street_address,
        city: formData.city,
        province: formData.province,
        postal_code: formData.postal_code,
        about_company: formData.about_company,
        mission_statement: formData.mission_statement,
        company_culture: formData.company_culture,
        values: formData.values,
        benefits: formData.benefits,
        typical_hiring_timeline: formData.typical_hiring_timeline,
        interview_process: formData.interview_process,
      };

      // First try to create — if profile already exists, update instead
      let response = await fetch(`${API}/recruiter-profile/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });

      // Profile already exists — update it instead
      if (response.status === 400) {
        const errData = await response.json();
        if (errData.detail === 'Profile already exists') {
          response = await fetch(`${API}/recruiter-profile/me/`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(profileData)
          });
        }
      }

      if (response.status === 401 || response.status === 403) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => {
          localStorage.removeItem('token');
          window.location.href = '/recruiter/login';
        }, 2000);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error (${response.status})`);
      }

      navigate('/recruiter/dashboard');
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Network error — please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to create profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="step-content">
      <h2>Company Basics</h2>
      <p className="step-description">Tell us about your company</p>

      <div className="form-group">
        <label>Company Name *</label>
        <input
          type="text"
          name="company_name"
          value={formData.company_name}
          onChange={handleInputChange}
          placeholder="Acme Corporation"
          required
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Industry *</label>
          <select
            name="industry"
            value={formData.industry}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Industry</option>
            {industries.map(ind => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Company Size</label>
          <select
            name="company_size"
            value={formData.company_size}
            onChange={handleInputChange}
          >
            <option value="">Select Size</option>
            {companySizes.map(size => (
              <option key={size} value={size}>{size} employees</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Founded Year</label>
          <input
            type="number"
            name="founded_year"
            value={formData.founded_year}
            onChange={handleInputChange}
            placeholder="2020"
            min="1800"
            max={new Date().getFullYear()}
          />
        </div>

        <div className="form-group">
          <label>Company Website</label>
          <input
            type="text"
            name="company_website"
            value={formData.company_website}
            onChange={handleInputChange}
            placeholder="https://www.company.com"
          />
        </div>

        <div className="form-group">
          <label>CIPC Registration Number *</label>
          <input
            type="text"
            name="registration_number"
            value={formData.registration_number || ''}
            onChange={handleInputChange}
            placeholder="e.g., 2021/123456/07"
            required
          />
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.35rem' }}>
            <i className="fas fa-shield-alt" style={{ marginRight: '0.3rem', color: '#6366f1' }}></i>
            Required to verify your company is registered with CIPC. Protects job seekers from fraudulent listings.{' '}
            <a href="https://www.cipc.co.za" target="_blank" rel="noreferrer" style={{ color: '#6366f1' }}>
              Find your number →
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <h2>Contact Information</h2>
      <p className="step-description">How can candidates reach you?</p>

      <div className="form-row">
        <div className="form-group">
          <label>Contact Person Name *</label>
          <input
            type="text"
            name="contact_person_name"
            value={formData.contact_person_name}
            onChange={handleInputChange}
            placeholder="John Doe"
            required
          />
        </div>

        <div className="form-group">
          <label>Job Title</label>
          <input
            type="text"
            name="contact_person_title"
            value={formData.contact_person_title}
            onChange={handleInputChange}
            placeholder="HR Manager"
          />
        </div>
      </div>

      <div className="form-group">
        <label>Phone Number *</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="+27 123 456 789"
          required
        />
      </div>

      <div className="form-group">
        <label>Street Address</label>
        <input
          type="text"
          name="street_address"
          value={formData.street_address}
          onChange={handleInputChange}
          placeholder="123 Main Street"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            placeholder="Johannesburg"
          />
        </div>

        <div className="form-group">
          <label>Province</label>
          <select
            name="province"
            value={formData.province}
            onChange={handleInputChange}
          >
            <option value="">Select Province</option>
            {provinces.map(prov => (
              <option key={prov} value={prov}>{prov}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Postal Code</label>
        <input
          type="text"
          name="postal_code"
          value={formData.postal_code}
          onChange={handleInputChange}
          placeholder="2000"
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="step-content">
      <h2>Company Details</h2>
      <p className="step-description">Help candidates understand your company culture</p>

      <div className="form-group">
        <label>About Company</label>
        <textarea
          name="about_company"
          value={formData.about_company}
          onChange={handleInputChange}
          rows="4"
          placeholder="Describe your company, what you do, and what makes you unique..."
        />
      </div>

      <div className="form-group">
        <label>Mission Statement</label>
        <textarea
          name="mission_statement"
          value={formData.mission_statement}
          onChange={handleInputChange}
          rows="3"
          placeholder="What is your company's mission?"
        />
      </div>

      <div className="form-group">
        <label>Company Culture</label>
        <textarea
          name="company_culture"
          value={formData.company_culture}
          onChange={handleInputChange}
          rows="3"
          placeholder="Describe your work environment and culture..."
        />
      </div>

      <div className="form-group">
        <label>Company Values</label>
        <div className="value-input-group">
          <input
            type="text"
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            placeholder="e.g., Innovation, Integrity, Teamwork"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addValue())}
          />
          <button type="button" onClick={addValue} className="btn-add">Add</button>
        </div>
        <div className="tags-list">
          {formData.values.map((value, index) => (
            <span key={index} className="tag">
              {value}
              <button type="button" onClick={() => removeValue(index)}>×</button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="step-content">
      <h2>Benefits & Hiring Process</h2>
      <p className="step-description">What do you offer to employees?</p>

      <div className="form-group">
        <label>Company Benefits</label>
        <div className="benefits-grid">
          {commonBenefits.map(benefit => (
            <div
              key={benefit}
              className={`benefit-card ${formData.benefits.includes(benefit) ? 'selected' : ''}`}
              onClick={() => toggleBenefit(benefit)}
            >
              <i className={`fas fa-${formData.benefits.includes(benefit) ? 'check-circle' : 'circle'}`}></i>
              {benefit}
            </div>
          ))}
        </div>
        
        <div className="custom-benefit-input">
          <input
            type="text"
            value={currentBenefit}
            onChange={(e) => setCurrentBenefit(e.target.value)}
            placeholder="Add custom benefit"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomBenefit())}
          />
          <button type="button" onClick={addCustomBenefit} className="btn-add">Add Custom</button>
        </div>

        {formData.benefits.filter(b => !commonBenefits.includes(b)).length > 0 && (
          <div className="custom-benefits-list">
            <p>Custom Benefits:</p>
            <div className="tags-list">
              {formData.benefits.filter(b => !commonBenefits.includes(b)).map((benefit, index) => (
                <span key={index} className="tag">
                  {benefit}
                  <button type="button" onClick={() => removeBenefit(benefit)}>×</button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Typical Hiring Timeline</label>
        <input
          type="text"
          name="typical_hiring_timeline"
          value={formData.typical_hiring_timeline}
          onChange={handleInputChange}
          placeholder="e.g., 2-3 weeks"
        />
      </div>

      <div className="form-group">
        <label>Interview Process</label>
        <textarea
          name="interview_process"
          value={formData.interview_process}
          onChange={handleInputChange}
          rows="4"
          placeholder="Describe your interview process (e.g., Phone screening, Technical interview, Final interview with CEO...)"
        />
      </div>
    </div>
  );

  return (
    <div className="profile-setup-container recruiter-setup">
      <div className="setup-header">
        <div className="logo">
          <img src="/JobLinkedAI.png" alt="JobLinked AI" />
          <span>JobLinked AI - Recruiter</span>
        </div>
        <div className="progress-bar">
          <div className="progress" style={{ width: `${(currentStep / 4) * 100}%` }}></div>
        </div>
        <p className="step-indicator">Step {currentStep} of 4</p>
      </div>

      <div className="setup-content">
        <form onSubmit={(e) => e.preventDefault()}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            {currentStep > 1 && (
              <button type="button" onClick={handleBack} className="btn-secondary">
                <i className="fas fa-arrow-left"></i> Back
              </button>
            )}

            <div className="right-actions">
              {currentStep > 2 && currentStep < 4 && (
                <button type="button" onClick={handleSkip} className="btn-skip">
                  Skip
                </button>
              )}

              {currentStep < 4 ? (
                <button type="button" onClick={handleNext} className="btn-primary">
                  Next <i className="fas fa-arrow-right"></i>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating Profile...' : 'Complete Setup'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RecruiterProfileSetup;
